import { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import { Env } from '@contexts/AppContext';
import { Article, MostPopularArticle, ParsedTakeaway } from '@interfaces/homepage';
import { User } from '@utils/backend-api/currentUser';

export enum ArticleTheme {
  Data = 'data',
  Feature = 'feature',
  Graphic = 'graphic',
  Standard = 'standard',
  Weekend = 'weekend',
}

// TODO: Remove after experiment
// {@link https://app.asana.com/0/1201413338509198/1208029113890850/f}
// Unrelated to the SSR surface. No consumers in this codebase; remove when
// the experiment is confirmed complete.
export enum PaywallVariant {
  FullBleed = 'full_bleed',
  Standard = 'standard',
  TwoOption = 'two_option',
}

export enum MidArticleUnitTypes {
  DeepResearch = 'deep_research',
  Event = 'event',
  Recirc = 'recirc',
}

/**
 * Access state for an article, set server-side and passed on the article object.
 *
 * - 'locked': user cannot view full content (non-subscriber on paywalled article)
 * - 'seo-preview': SEO bot viewing a paywalled article — full text is served
 *   with the seo-paywall-content CSS class for indexing. Only applies to
 *   bot + paywalled article; a bot viewing a FREE article gets 'full'.
 * - 'full': user has full access (subscriber, free article, unlock link, etc.)
 *   Also applies to bots viewing free articles — no separate cache variant needed.
 */
export type AccessState = 'locked' | 'seo-preview' | 'full';

// Article fields that are always present regardless of access state.
type SSRArticleBase = Pick<
  Article,
  | 'id'
  | 'slug'
  | 'title'
  | 'subTitle'
  | 'highlight'
  | 'authors'
  | 'publishedAt'
  | 'picture'
  | 'pictureCaption'
  | 'pictureLink'
  | 'media'
  | 'isPro'
  | 'freeBlurb'
  | 'scriptList'
  | 'featuredGraphic'
>;

/**
 * Discriminated union for the article object in SSR props.
 *
 * The `access` discriminant determines which gated fields are present:
 * - 'locked': no fullText, takeaway, or addendum
 * - 'seo-preview': fullText served for SEO indexing (with CSS class)
 * - 'full': all content fields available
 *
 * Consumers must narrow on `access` before reading gated fields:
 *   if (article.access !== 'locked') { article.fullText; }
 */
export type SSRArticle =
  | (SSRArticleBase & { access: 'locked' })
  | (SSRArticleBase & {
      access: 'seo-preview';
      fullText: string;
      takeaway?: ParsedTakeaway[];
      addendum?: string;
    })
  | (SSRArticleBase & {
      access: 'full';
      fullText: string;
      takeaway?: ParsedTakeaway[];
      addendum?: string;
    });

/** Type guard: narrows SSRArticle to variants with content access (not locked). */
export const isUnlocked = (
  a: SSRArticle
): a is Extract<SSRArticle, { access: 'seo-preview' | 'full' }> => a.access !== 'locked';

/** Type guard: narrows SSRArticle to the SEO preview variant (bot viewing paywalled content). */
export const isSEOPreview = (a: SSRArticle): a is Extract<SSRArticle, { access: 'seo-preview' }> =>
  a.access === 'seo-preview';

/**
 * @deprecated New code must narrow on `article.access` directly. This helper
 * exists only so pre-union call sites compile during migration; delete it when
 * all consumers narrow on the discriminant themselves.
 *
 * Returns `{ fullText: null }` for locked articles (optional fields are omitted,
 * not set to undefined). Returns the actual gated fields otherwise.
 */
export function getGatedContent(article: SSRArticle): {
  fullText: string | null;
  takeaway?: ParsedTakeaway[];
  addendum?: string;
} {
  if (article.access === 'locked') {
    return { fullText: null };
  }
  return {
    fullText: article.fullText,
    takeaway: article.takeaway,
    addendum: article.addendum,
  };
}

/**
 * Props that will form the cached SSR surface once SSR caching is enabled.
 *
 * Every prop here must be deterministic from the planned cache key inputs:
 *   1. article.id
 *   2. article.updated_at || content_updated_at
 *   3. access ('locked' | 'seo-preview' | 'full')
 *   4. isWebview
 *   5. deploy version (CACHE_PREFIX in ssr_helper.rb)
 *
 * `theme` is derived from article data + isWebview, so it does not add a
 * cache dimension — it is fully determined by the inputs above.
 *
 * Props that are NOT deterministic from these inputs (per-user, per-session,
 * or time-varying) belong in UnmigratedArticleProps until they can be moved
 * to a client-side fetch after hydration.
 *
 * ── Access-gated content (fullText, takeaway, addendum) ──
 * Enforced by the SSRArticle discriminated union: these fields only exist
 * on the 'seo-preview' and 'full' variants. The `access` discriminant
 * replaces the former canViewFullArticle and isSEOBotAndArticleIsNotFree
 * booleans:
 *   access === 'full'        → was canViewFullArticle (non-bot)
 *   access === 'seo-preview' → was isSEOBotAndArticleIsNotFree
 *   access !== 'locked'      → was canViewFullArticle (either bot or user)
 */
export interface ArticleSSRProps {
  article: SSRArticle;
  theme: ArticleTheme; // Cache-safe: deterministic from article.theme,
  // article.featured_graphic, and session.is_webview (a cache key dimension).
  // Resolved by ArticlePresenter#set_article_theme.
  isWebview: boolean; // Webview sessions force theme to 'feature' for standard/weekend
  // articles and change article padding. LockedArticle also checks this, but only
  // renders post-hydration so it does not affect cached SSR HTML. DeepResearch's
  // isWebview gating reads from AppContext, not this prop.
  // SSR-relevant effects: theme forcing and padding only.
  env: Env; // Deployment-level, same for all requests. Needed for imgix URLs.
}

/**
 * Props that have not yet been adapted to the SSR architecture.
 *
 * Each prop here is still passed from Rails but is not part of the cached SSR
 * surface. Props should be moved out by either:
 *   - Moving to ArticleSSRProps (if needed for the cached render)
 *   - Moving to a client-side API fetch after hydration
 *   - Eliminating it entirely
 *
 * When removing a prop, also remove the server-side query/computation in the
 * controller and view. Deleting from the interface compiles cleanly while the
 * Rails controller keeps fetching — verify both sides.
 *
 * If non-SSR data is still needed server-side for other reasons, consider
 * serializing it into the DOM and seeding the React Query client from the DOM
 * rather than re-fetching via API.
 */
interface UnmigratedArticleProps {
  article: Omit<Article, 'fullText' | 'takeaway' | 'addendum'>; // Omit gated fields so the
  // intersection with SSRArticle does not unconditionally re-add them. The
  // discriminated union on SSRArticle controls their presence.
  hideAwarenessBar: boolean; // Move to client after refactoring ArticleLayout padding.
  // Currently affects SSR-time padding: the locked-article padding branch in
  // ArticleLayout's <article> element chooses between pt-90 and pt-155 based on
  // this value. Moving to client without addressing padding causes visible layout
  // shift. To migrate: make padding awareness-bar-independent, or add as a cache
  // key dimension.
  initialCurrentUser?: User; // Per-user. Before enabling SSR caching, audit all
  // useCurrentUser() and useAppContext() calls under ArticleLayout to confirm
  // none read user data during SSR. If any do, the cache varies per user and
  // becomes worthless.
  isGdprRequired: boolean; // Move to client, but only after confirming no component
  // under ArticleLayout reads this value during SSR. Moving a prop to context does
  // not remove it from the SSR input closure — migration is only cache-safe if
  // consuming components either render outside the cached article tree, or gate
  // rendering on a client-side hydrated check so server HTML is invariant to the
  // value. Grep for SSR-time reads under ArticleLayout before treating as migrated.
  isNativeApp?: boolean; // Move to client after gating SSR-time reads.
  // useNativeAppVersionCheck() is synchronous and runs at render time, not after
  // hydration. Components that read it affect initial HTML: the isLegacyNativeApp
  // branch in Content/DataContent, the native-app conditionals in ArticleDetails,
  // and the bookmark visibility guard in Bookmark/index.tsx. To migrate: wrap all
  // useNativeAppVersionCheck() consumers in a hydrated check so server HTML is
  // invariant to the value, or add as a cache key dimension.
  mostPopularArticles: MostPopularArticle[]; // Move to client-side API fetch: site-level
  // data (global trending articles, refreshed hourly) that changes independently
  // of article content. Baking it into the article cache means either stale rankings
  // for the cache TTL, or invalidating article HTML when rankings change. Fetch via
  // API after hydration with its own cache TTL instead.
  nativeAppVersion?: string; // Move to client with isNativeApp: both are consumed by
  // useNativeAppVersionCheck() which runs at render time. Same migration path —
  // gate consumers behind a hydrated check.
  newsletterId?: string | number; // Article-level. Downstream components read this
  // top-level prop instead of article.newsletterSlug. Migration to newsletterSlug
  // is tracked outside this file; when complete, remove from here and ThemeViewsProps.
  relatedArticles: Article[]; // Move to client-side API fetch: article-level but changes
  // independently of article content (when related articles are published/updated).
  // Same reasoning as mostPopularArticles — fetch via API after hydration with its
  // own cache TTL. Not above the fold or SEO-relevant.
  midArticleUnitType?: MidArticleUnitTypes; // Move to client with relatedEvent.
  // Not purely article-content-derived: depends on @related_event (tag-based,
  // time-varying) and @is_advertisement_visible (session-dependent). Determines
  // which mid-article component renders (DeepResearch, Event, or Recirc).
  // Moving unit selection client-side means the server renders no mid-article
  // unit at all — DeepResearch and Recirc pop in post-hydration, not just Event.
  // The slot is below the initial viewport (scroll-depth assumption); if unit
  // height varies, reserve stable slot dimensions to avoid mid-scroll reflow.
  // DeepResearch's isWebview gating (MidArticle/DeepResearch/index.tsx) also
  // moves client-side with selection.
  relatedEvent?: MidArticleEventProps; // Move to client-side API fetch: tag-based and
  // time-varying (upcoming events matching article tags). Same reasoning as
  // mostPopularArticles. Not above the fold. When this moves client-side,
  // midArticleUnitType should move with it. Moving both client-side means the
  // server renders no mid-article unit — all three variants (DeepResearch,
  // Event, Recirc) pop in post-hydration.
  showArticleGiftButton?: boolean; // Per-user experiment (article_gift_button layer).
  // Move to client after confirming no component under ArticleLayout reads this
  // during SSR. Same safety condition as showBookmarksFlagEnabled: moving to
  // context does not remove it from the SSR input closure.
  showBookmarksFlagEnabled?: boolean; // Move to client, but only after confirming no
  // component under ArticleLayout reads this value during SSR. Same safety condition
  // as isGdprRequired: moving to context does not remove it from the SSR input
  // closure. Grep for SSR-time reads under ArticleLayout before treating as migrated.
  showUpsellPaywall: boolean; // Move to client after refactoring ArticleLayout padding.
  // The Paywall component itself is correctly lazy-loaded in ArticleLayout, but
  // showUpsellPaywall also affects SSR-time padding in ArticleLayout's <article>
  // element (pt-32 vs pt-90). To migrate: make padding independent of this prop,
  // or add as a cache key dimension.
}

/**
 * The full set of props Rails currently passes to the Article component.
 *
 * ArticleSSRProps is the target cached SSR surface. UnmigratedArticleProps
 * contains props still being adapted — each should eventually move to
 * client-side fetching, into ArticleSSRProps, or be eliminated.
 *
 * Both interfaces declare `article`. ArticleSSRProps uses the SSRArticle
 * discriminated union; UnmigratedArticleProps uses Omit<Article, gated fields>.
 * The intersection preserves the discriminated union while adding the remaining
 * Article fields (advertisement, contentType, etc.).
 */
export type ArticleProps = ArticleSSRProps & UnmigratedArticleProps;

/**
 * Props ArticleLayout passes to layout components (Standard, Data, Feature,
 * Graphic, Weekend). Derived from ArticleProps via Pick so that changes to
 * ArticleProps automatically flow through.
 *
 * The `article` field preserves the SSRArticle discriminated union via the
 * intersection. Layout components must narrow on `article.access` before
 * reading gated fields (fullText, takeaway, addendum).
 *
 * As props migrate out of UnmigratedArticleProps, update this Pick list to
 * reflect where each prop now lives (ArticleSSRProps, client fetch, or removed).
 *
 * End-state: when Rails narrows the serialized payload to SSRArticle, re-derive
 * the `article` member here from `ArticleSSRProps['article']` instead of the
 * intersection. The resulting compile errors enumerate every component depending
 * on non-SSR article fields and serve as the migration checklist. Until then,
 * the intersection exposes the full Article shape (including newsletterSlug and
 * all non-gated fields) with no type-level signal of what is actually in the
 * SSR surface.
 */
export type ThemeViewsProps = Pick<
  ArticleProps,
  | 'article'
  | 'env'
  | 'isWebview'
  | 'midArticleUnitType'
  | 'mostPopularArticles'
  | 'newsletterId'
  | 'relatedArticles'
  | 'relatedEvent'
>;

// Compile-time disjointness guard: no prop should appear in both interfaces
// (except `article`, which is intentionally in both — SSR provides the
// discriminated union, Unmigrated provides Omit<Article, gated fields>).
type SharedKeys = Exclude<Extract<keyof ArticleSSRProps, keyof UnmigratedArticleProps>, 'article'>;
type AssertDisjoint = [SharedKeys] extends [never]
  ? true
  : `ERROR: key appears in both ArticleSSRProps and UnmigratedArticleProps — decide which owns it: ${SharedKeys &
      string}`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _verifyDisjoint: AssertDisjoint = true;
